import { type ReactElement, useState, useEffect, useContext } from 'react'
// import { type MetaTransactionData } from '@safe-global/safe-core-sdk-types'
import {
  Grid,
  styled,
  Typography,
  Button,
  Box,
  Input,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Radio,
} from '@mui/material'
import classnames from 'classnames'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { type RuleInfo, RuleType } from '@/store/setUpInheritorsSlice'

import { encodeSetRuleFunc } from '@/utils/inherit-manager'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import 'dayjs/locale/en-gb'

import dayjs, { type Dayjs } from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

import { Body2White, Body1White } from '@/components/typography'
import css from './styles.module.css'

import useChainId from '@/hooks/useChainId'
import SendProcessing from '@/components/inheritors/SendProcessing'
import { SendProcessingContext } from '@/components/inheritors/SendProcessing'
import { PendingStatus } from '@/store/pendingTxsSlice'
import { ModalButtonContext } from '@/components/common/ModalButton'
import { trackEvent, INHERIT_EVENTS } from '@/services/analytics'
import { formatDate } from '@/utils/date'

dayjs.extend(utc)
dayjs.extend(timezone)
const tomorrow = dayjs().add(1, 'day')

const StyledDatePicker = styled(DatePicker)({
  '>.MuiInputBase-root': {
    color: 'var(--Ebony-Clay-600, #5B6B9C)',
    borderRadius: '4px',
    borderWidth: 0,
    backgroundColor: 'var(--Supporting-Dark-BG-700, #272A3E)',
  },
  '>.MuiInputBase-root::hover': {
    backgroundColor: 'var(--Supporting-Dark-BG-700, #272A3E)',
  },
})

const SetInheritanceRuleComponent = ({
  rule,
  onSendSuccess,
}: {
  rule: RuleInfo
  onSendSuccess: () => void
}): ReactElement => {
  const [ruleType, setRuleType] = useState(rule.type)
  const [timestamp, setTimestamp] = useState(rule.timestamp === 0 ? 0 : rule.timestamp * 1000)
  const [periodType, setPeriodType] = useState(rule.period.type)
  const [periodValue, setPeriodValue] = useState(rule.period.value)
  // const [metaTxData, setMetaTxData] = useState<MetaTransactionData | undefined>(undefined)
  // const [fee, setFee] = useState('')
  // const [feeError, setFeeError] = useState('')

  const { isSending, sendTx, sendError, txStatus } = useContext(SendProcessingContext)
  const { setModalOpen } = useContext(ModalButtonContext)

  const chainId = useChainId()

  useEffect(() => {
    if (isSending) {
      if (txStatus === PendingStatus.INDEXING) {
        onSendSuccess()
        setModalOpen(false)
      }
    }
  }, [txStatus, isSending, sendError])

  const onExpand = (id: number) => (_: any, isExpanded: boolean) => {
    if (isExpanded) setRuleType(id)
  }

  const onDatePickerChange = (date: Dayjs) => {
    setTimestamp(date.valueOf())
  }

  const onSetPeriodType = (type: 'month' | 'year') => {
    setPeriodType(type)
  }

  const onPeriodValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value === '' ? 0 : Number(event.target.value)
    setPeriodValue(newValue)
  }

  const handleConfirm = () => {
    const newRule: RuleInfo = {
      type: ruleType,
      timestamp: ruleType === RuleType.OnSpecificDate ? timestamp : 0,
      period: {
        type: periodType,
        value: ruleType === RuleType.AfterInactivityPeriod ? periodValue : 0,
      },
    }

    const ruleTypeLabel = newRule.type === RuleType.OnSpecificDate ? 'OnSpecificDate' : 'AfterInactivityPeriod'
    const ruleValueLabel =
      newRule.type === RuleType.OnSpecificDate
        ? formatDate(newRule.timestamp)
        : `${newRule.period.value} ${newRule.period.type}`

    trackEvent({ ...INHERIT_EVENTS.CHANGED_RULE, ...{ label: `${ruleTypeLabel}_${ruleValueLabel}` } })
    const txParams = encodeSetRuleFunc(newRule, chainId)
    void sendTx(txParams, true)
  }

  const handleBack = () => {
    setModalOpen(false)
  }

  const isDisabledNext = () => {
    if (
      ruleType === RuleType.Unknown ||
      (ruleType === RuleType.OnSpecificDate && !timestamp) ||
      (ruleType === RuleType.AfterInactivityPeriod && periodValue === 0)
    ) {
      return true
    } else {
      if (ruleType === rule.type) {
        if (ruleType === RuleType.OnSpecificDate && timestamp === rule.timestamp) {
          return true
        }
        if (
          ruleType === RuleType.AfterInactivityPeriod &&
          periodValue === rule.period.value &&
          periodType === rule.period.type
        ) {
          return true
        }
      }
    }
    return false
  }

  const renderFooter = () => {
    return (
      <Grid item xs={12} mt="16px">
        <Grid container>
          <Grid item xs={5} sm={4}>
            <Button
              variant="text"
              sx={{
                paddingY: '10px',
                width: '100%',
                borderRadius: '4px',
                background: 'var(--Ebony-Clay-950, #2F354B)',
                color: 'var(--Ebony-Clay-300, #D2D7E5)',
                ':hover': {
                  background: 'rgba(255, 255, 255, 0.08)',
                },
              }}
              disabled={isSending}
              onClick={handleBack}
            >
              Cancel
            </Button>
          </Grid>
          <Grid item xs={2} sm={4}></Grid>
          <Grid item xs={5} sm={4} textAlign="right">
            <Button
              disabled={isDisabledNext() || isSending}
              variant="contained"
              sx={{
                paddingY: '10px',
                width: '100%',
              }}
              onClick={handleConfirm}
            >
              Confirm
            </Button>
          </Grid>
        </Grid>
      </Grid>
    )
  }

  return (
    <SendProcessing>
      <Grid container>
        <Grid item xs={12} mt="16px">
          <Box>
            <Typography
              sx={{
                color: 'var(--Ebony-Clay-300, #D2D7E5)',
                fontSize: '16px',
                fontWeight: '400',
                fontStyle: 'normal',
                lineHeight: '160%',
              }}
            >
              When would you like your inheritance to be divided?
            </Typography>
            <Box
              display="flex"
              flexDirection="column"
              flexWrap="wrap"
              justifyContent="center"
              alignItems="center"
              mt={2}
            >
              <Accordion
                key={1}
                expanded={ruleType === RuleType.OnSpecificDate}
                onChange={onExpand(RuleType.OnSpecificDate)}
              >
                <AccordionSummary
                  sx={{ paddingLeft: 0 }}
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1-content"
                  id="panel1-header"
                >
                  <Box display="flex" flexDirection="row" justifyContent="center" alignItems="center">
                    <Radio sx={{ mx: '8px' }} checked={ruleType === RuleType.OnSpecificDate} />
                    <Box py="8px">
                      <Body1White>On a specific date</Body1White>
                      <Body2White sx={{ color: 'var(--Ebony-Clay-500, #7C8AB4)' }}>
                        Set a rule based on an exact date for inheritance distribution.
                      </Body2White>
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box px="32px" pb="8px" sx={{ width: '100%' }}>
                    <Body2White mb="4px" sx={{ color: 'var(--Ebony-Clay-400, #AAB3CF)' }}>
                      On this exact date
                    </Body2White>
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
                      <StyledDatePicker
                        disablePast={true}
                        minDate={tomorrow}
                        slotProps={{
                          textField: {
                            variant: 'filled',
                            inputProps: {
                              sx: { paddingX: '16px', paddingY: '10px', fontSize: '14px' },
                              placeholder: 'DD/MM/YYYY',
                            },
                            InputProps: {
                              disableUnderline: true,
                              sx: { ':hover': { backgroundColor: 'var(--Supporting-Dark-BG-700, #272A3E)' } },
                            },
                          },
                          openPickerIcon: { sx: { color: 'var(--Ebony-Clay-500, #7C8AB4)' } },
                          popper: { placement: 'bottom-end' },
                        }}
                        sx={{ width: '100%' }}
                        onAccept={(date: unknown) => onDatePickerChange(date as Dayjs)}
                        value={timestamp === 0 ? null : dayjs(timestamp)}
                      />
                    </LocalizationProvider>
                  </Box>
                </AccordionDetails>
              </Accordion>
              <Accordion
                key={2}
                expanded={ruleType === RuleType.AfterInactivityPeriod}
                onChange={onExpand(RuleType.AfterInactivityPeriod)}
              >
                <AccordionSummary
                  sx={{ paddingLeft: 0 }}
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel2-content"
                  id="panel2-header"
                >
                  <Box display="flex" flexDirection="row" justifyContent="center" alignItems="center">
                    <Radio sx={{ mx: '8px' }} checked={ruleType === RuleType.AfterInactivityPeriod} />
                    <Box py="8px">
                      <Body1White>After inactivity period</Body1White>
                      <Body2White sx={{ color: 'var(--Ebony-Clay-500, #7C8AB4)' }}>
                        Set a rule for inheritance after a specified on-chain inactivity.
                      </Body2White>
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box px="32px" pb="8px" sx={{ width: '100%' }}>
                    <Body2White mb="4px" sx={{ color: 'var(--Ebony-Clay-400, #AAB3CF)' }}>
                      Inactivity period
                    </Body2White>
                    <Grid
                      container
                      justifyContent="space-between"
                      p="5px"
                      sx={{
                        borderRadius: '4px',
                        backgroundColor: 'var(--Supporting-Dark-BG-700, #272A3E)',
                      }}
                    >
                      <Grid item xs>
                        <Input
                          disableUnderline
                          placeholder="0"
                          value={Number(periodValue).toString()}
                          sx={{
                            width: '100%',
                            paddingX: '16px',
                            fontSize: '14px',
                            backgroundColor: 'var(--Supporting-Dark-BG-700, #272A3E)',
                            borderRadius: '4px',
                            color: 'var(--Ebony-Clay-300, #D2D7E5)',
                          }}
                          inputProps={{
                            type: 'number',
                            min: 0,
                            step: 1,
                          }}
                          onChange={onPeriodValueChange}
                        />
                      </Grid>
                      <Grid item container xs="auto" textAlign="right" alignContent="center">
                        <Button
                          className={classnames(css.monthBtn, periodType !== 'month' && css.btnInactive)}
                          sx={{
                            ':hover': {
                              color: 'var(--Ebony-Clay-300, #D2D7E5)',
                              background: 'var(--Ebony-Clay-800, #3B4569)',
                            },
                          }}
                          variant="text"
                          onClick={() => onSetPeriodType('month')}
                        >
                          Month
                        </Button>
                        <Button
                          className={classnames(css.yearBtn, periodType !== 'year' && css.btnInactive)}
                          variant="text"
                          onClick={() => onSetPeriodType('year')}
                          sx={{
                            ':hover': {
                              color: 'var(--Ebony-Clay-300, #D2D7E5)',
                              background: 'var(--Ebony-Clay-800, #3B4569)',
                            },
                          }}
                        >
                          Year
                        </Button>
                      </Grid>
                    </Grid>
                    {periodValue === 0 && (
                      <Body2White
                        sx={{ color: 'var(--Supporting-Carnation, #FF2239)' }}
                      >{`The ${periodType} must be greater than 0`}</Body2White>
                    )}
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Box>
          </Box>
        </Grid>
        {sendError !== '' && (
          <Body2White sx={{ color: 'var(--Supporting-Carnation, #FF2239)', marginTop: '8px' }}>{sendError}</Body2White>
        )}
        {renderFooter()}
      </Grid>
    </SendProcessing>
  )
}

export default SetInheritanceRuleComponent
